import React, { useEffect } from "react";
import { useState } from "react";
import axios from "axios";
import { base_url } from "../../config";
import badgeImg from "../../assets/badge.png";
import { PrinterIcon } from "lucide-react";

interface Pass {
  id: string;
  name: string;
  affiliation: string;
  category: string;
  events: {
    reception: boolean;
    studentReception: boolean;
    banquet: boolean;
    banquetType?: "poultry" | "vegan";
    industryDay: boolean;
  };
  otherAccess: string;
}

interface MiniBadgeProps {
  name: string;
  affiliation: string;
  category: string;
  code: string;
}

const MiniBadge: React.FC<MiniBadgeProps> = ({ name, affiliation, category, code }) => {
  return (
    <div style={{ width: "250px" }}>
      <div

        className="rounded-lg p-4 space-y-3 h-[200px] flex flex-col"
        style={{
          background: `url(${badgeImg}) no-repeat`,
          backgroundSize: "contain",
          backgroundPosition: "center",
        }}>
        <div style={{ marginLeft: "auto" }}>
          {code}
        </div>
        <br />
        <div style={{ margin: "auto", textAlign: "center" }}>
          <div className="text-lg font-semibold text-gray-900">{name}</div>
          <div className="text-sm text-gray-600">{affiliation}</div>
          <div className="text-xs text-gray-500 mt-4 text-white">{category}</div>
        </div>
      </div>
    </div>
  );
}

export default function PassManagement() {
  const [passes, setPasses] = useState<Pass[]>([]);

  const [formData, setFormData] = useState({
    name: "",
    affiliation: "",
    category: "",
    events: {
      reception: false,
      studentReception: false,
      banquet: false,
      banquetType: "poultry" as "poultry" | "vegan",
      industryDay: false,
    },
    otherAccess: "",
  });

  const handleEventChange = (event: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      events: {
        ...prev.events,
        [event]: checked,
        ...(event === "banquet" && !checked ? { banquetType: "poultry" } : {}),
      },
    }));
  };

  const handleBanquetTypeChange = (type: "poultry" | "vegan") => {
    setFormData((prev) => ({
      ...prev,
      events: {
        ...prev.events,
        banquetType: type,
      },
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.affiliation || !formData.category) {
      return;
    }

    const newPass: Pass = {
      id: Date.now().toString(),
      name: formData.name,
      affiliation: formData.affiliation,
      category: formData.category,
      events: formData.events,
      otherAccess: formData.otherAccess,
    };
    try {
      const response = await axios.post(
        `${base_url}/api/admin/post_extra`,
        newPass
      );
      newPass.id = response.data.data.pass_id;
      setPasses((prev) => [...prev, newPass]);

      // Reset form
      setFormData({
        name: "",
        affiliation: "",
        category: "",
        events: {
          reception: false,
          studentReception: false,
          banquet: false,
          banquetType: "poultry",
          industryDay: false,
        },
        otherAccess: "",
      });
    } catch (error) {
      console.error("Error creating pass:", error);
      return;
    }
  };

  const handleBadgePrint = async (badge:any) => {
    
    try {
      const response = await axios.post(`${base_url}/api/admin/print_press_badge_mt`, {
        id: badge,
      }, {
        responseType: 'blob' // Important: tell axios to expect a binary response
      });

      // Create a blob URL from the response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);

      // Create a temporary link and trigger the download
      const link = document.createElement('a');
      link.href = url;
      link.download = `badge_${badge.id}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (error) {
      console.error("Error printing badge:", error);
    }
  };

  const removePass = async (id: string) => {
    try {
      await axios.post(`${base_url}/api/admin/remove_extra`, { id });
      setPasses((prev) => prev.filter((pass) => pass.id !== id));
    } catch (error) {
      console.error("Error deleting pass:", error);
      return;
    }
  };

  const getEventsList = (events: Pass["events"]) => {
    const eventList = [];
    if (events.reception) eventList.push("Reception");
    if (events.studentReception) eventList.push("Student Reception");
    if (events.banquet) eventList.push(`Banquet (${events.banquetType})`);
    if (events.industryDay) eventList.push("Industry Day");
    return eventList;
  };

  useEffect(() => {
    axios
      .get(`${base_url}/api/admin/get_extra`)
      .then((response) => {
        setPasses(response.data || []);
      })
      .catch((error) => {
        console.error("Error fetching exhibitors :", error);
      });
  }, []);

  //   const handleSave = async () => {
  //     setSaving(true);
  //     setSaveMessage("");
  //     try {
  //       await axios.post(`${base_url}/api/admin/exhibitors`, { text });
  //       setSaveMessage("Sponsors list updated successfully!");
  //     } catch (error) {
  //       setSaveMessage("Error updating sponsors list.");
  //     } finally {
  //       setSaving(false);
  //     }
  //   };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8">
      {/* Form Section */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-2">
            <svg
              className="h-5 w-5 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
              />
            </svg>
            <h2 className="text-xl font-semibold text-gray-900">
              Define New Pass
            </h2>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Create a new pass with specific event access permissions
          </p>
        </div>
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Enter full name"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="affiliation"
                  className="block text-sm font-medium text-gray-700"
                >
                  Affiliation
                </label>
                <input
                  type="text"
                  id="affiliation"
                  value={formData.affiliation}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      affiliation: e.target.value,
                    }))
                  }
                  placeholder="University, Company, etc."
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="space-y-2">
                <label
                  htmlFor="category"
                  className="block text-sm font-medium text-gray-700"
                >
                  Pass Category (e.g. Press, VIP...)
                </label>
                <input
                  type="text"
                  id="category"
                  value={formData.category}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      category: e.target.value,
                    }))
                  }
                  placeholder="Enter pass category"
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="space-y-4">
              <label className="block text-base font-medium text-gray-700">
                Event Access
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="reception"
                    checked={formData.events.reception}
                    onChange={(e) =>
                      handleEventChange("reception", e.target.checked)
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="reception"
                    className="text-sm font-medium text-gray-700"
                  >
                    Reception
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="studentReception"
                    checked={formData.events.studentReception}
                    onChange={(e) =>
                      handleEventChange("studentReception", e.target.checked)
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="studentReception"
                    className="text-sm font-medium text-gray-700"
                  >
                    Student Reception
                  </label>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="banquet"
                      checked={formData.events.banquet}
                      onChange={(e) =>
                        handleEventChange("banquet", e.target.checked)
                      }
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label
                      htmlFor="banquet"
                      className="text-sm font-medium text-gray-700"
                    >
                      Banquet
                    </label>
                  </div>
                  {formData.events.banquet && (
                    <div className="ml-6 space-y-2">
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="poultry"
                          name="banquetType"
                          value="poultry"
                          checked={formData.events.banquetType === "poultry"}
                          onChange={() => handleBanquetTypeChange("poultry")}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <label
                          htmlFor="poultry"
                          className="text-sm font-medium text-gray-700"
                        >
                          Poultry
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="vegan"
                          name="banquetType"
                          value="vegan"
                          checked={formData.events.banquetType === "vegan"}
                          onChange={() => handleBanquetTypeChange("vegan")}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                        />
                        <label
                          htmlFor="vegan"
                          className="text-sm font-medium text-gray-700"
                        >
                          Vegan
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="industryDay"
                    checked={formData.events.industryDay}
                    onChange={(e) =>
                      handleEventChange("industryDay", e.target.checked)
                    }
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label
                    htmlFor="industryDay"
                    className="text-sm font-medium text-gray-700"
                  >
                    Industry Day
                  </label>
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <label className="block text-base font-medium text-gray-700">
                Other Access <br />
                <span className="text-sm text-gray-500">
                  (e.g. access to specific events, like one session or talk)
                </span>
              </label>
              <input
                type="text"
                id="otherAccess"
                value={formData.otherAccess}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, otherAccess: e.target.value }))
                }
                placeholder="Optionally describe other access permissiions"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              type="submit"
              className="w-full md:w-auto px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
            >
              Create Pass
            </button>
          </form>
        </div>
      </div>

      {/* Passes List Section */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-2">
            <svg
              className="h-5 w-5 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3a4 4 0 118 0v4m-4 8a2 2 0 100-4 2 2 0 000 4zm0 0v3m-3-3h6m-1 1l1-1 1 1"
              />
            </svg>
            <h2 className="text-xl font-semibold text-gray-900">
              Defined Passes ({passes.length})
            </h2>
          </div>
          <p className="text-sm text-gray-600 mt-1">
            Manage all created passes and their event access
          </p>
        </div>
        <div className="p-6">
          {passes.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <svg
                className="h-12 w-12 mx-auto mb-4 opacity-50"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                />
              </svg>
              <p>No passes defined yet. Create your first pass above.</p>
            </div>
          ) : (
            <div className="space-y-4">

              {passes.map((pass) => (
                <div
                  key={pass.id}
                  className="border border-gray-200 rounded-lg p-4 space-y-3 flex"
                >
                  <div>
                    <MiniBadge
                      name={pass.name}
                      affiliation={pass.affiliation}
                      category={pass.category}
                      code={pass.id}
                    />
                  </div>


                  <div className="space-y-2 grow ml-2">
                    <label className="text-sm font-medium text-gray-700 ">
                      Event Access:
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {getEventsList(pass.events).map((event, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200"
                        >
                          {event}
                        </span>
                      ))}
                      {getEventsList(pass.events).length === 0 && (
                        <span className="text-sm text-gray-500">
                          No events selected
                        </span>
                      )}
                    </div>
                    <div className="pt-2 text-sm font-medium text-gray-700 ">
                      Other Access:
                    </div>
                    <div className="text-sm text-gray-600">
                      {pass.otherAccess || "No additional access permissions"}
                    </div>


                  </div>

                  <div className="flex items-start  flex-col gap-2">
                    <div className="flex flex-col items-center w-[60px]">
                      <button
                        style={{ width: "40px" }}
                        onClick={() => {
                          if (window.confirm("Are you sure you want to remove this pass?")) {
                            removePass(pass.id);
                          }
                        }}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-red-600 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                      >
                        <svg
                          className="h-5 w-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                    <div className="flex flex-col items-center w-[60px]">
                      <button
                        style={{ width: "40px" }}
                        onClick={() => handleBadgePrint(pass.id)}
                        className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-red-600 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 transition-colors"
                      >
                        <PrinterIcon className="h-4 w-4" />
                      </button>
                      <span className="text-xs text-gray-500">Badge</span>
                    </div>
                  </div>

                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
